import React, { useState, useMemo, useCallback } from "react";
import {
    ComposableMap,
    Geographies,
    Geography,
    ZoomableGroup,
} from "react-simple-maps";
import "./WorldOnboardingMap.css";

// ─── TopoJSON source ──────────────────────────────────────────────────────────
const GEO_URL =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ─── Geography style objects (react-simple-maps API, module-level constants) ──
const GEO_STYLE_DEFAULT = {
    default: {
        fill: "#E2E8F0",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        transition: "fill 0.2s ease",
    },
    hover: {
        fill: "#CBD5E1",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        cursor: "pointer",
    },
    pressed: { fill: "#CBD5E1", outline: "none" },
};

const GEO_STYLE_ONBOARDED = {
    default: {
        fill: "#EF7D32",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        transition: "fill 0.2s ease",
    },
    hover: {
        fill: "#C44E00",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        cursor: "pointer",
    },
    pressed: { fill: "#C44E00", outline: "none" },
};

const GEO_STYLE_INPROGRESS = {
    default: {
        fill: "#EAB308",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        transition: "fill 0.2s ease",
    },
    hover: {
        fill: "#CA8A04",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        cursor: "pointer",
    },
    pressed: { fill: "#CA8A04", outline: "none" },
};

const GEO_STYLE_PLANNED = {
    default: {
        fill: "#8B5A2B",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        transition: "fill 0.2s ease",
    },
    hover: {
        fill: "#6D3B14",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        cursor: "pointer",
    },
    pressed: { fill: "#6D3B14", outline: "none" },
};

const GEO_STYLE_SELECTED = {
    default: {
        fill: "#C44E00",
        stroke: "#0F172A",
        strokeWidth: 1.5,
        outline: "none",
        transition: "all 0.2s ease",
        cursor: "pointer",
    },
    hover: {
        fill: "#A73E00",
        stroke: "#0F172A",
        strokeWidth: 1.8,
        outline: "none",
        cursor: "pointer",
    },
    pressed: { fill: "#8A3300", outline: "none" },
};

const getGeoStyle = (status, isSelected = false) => {
    if (isSelected) {
        return GEO_STYLE_SELECTED;
    }
    switch (status) {
        case "onboarded":
            return GEO_STYLE_ONBOARDED;
        case "inprogress":
            return GEO_STYLE_INPROGRESS;
        case "planned":
            return GEO_STYLE_PLANNED;
        default:
            return GEO_STYLE_DEFAULT;
    }
};

// Country aliases to normalize discrepancies between API names and TopoJSON properties
const COUNTRY_NAME_ALIASES = {
    "ivory coast": "côte d'ivoire",
    "cote d'ivoire": "côte d'ivoire",
    "united states": "united states of america",
    "usa": "united states of america",
    "uk": "united kingdom",
    "uae": "united arab emirates",
    "russia": "russian federation",
    "korea": "south korea",
    "republic of korea": "south korea",
    "democratic republic of the congo": "dem. rep. congo",
    "dr congo": "dem. rep. congo",
};

const normalizeCountryName = (name) => {
    if (!name) return "";
    const clean = name.toLowerCase().trim();
    return COUNTRY_NAME_ALIASES[clean] || clean;
};

// ─── Component ────────────────────────────────────────────────────────────────
// onboardedCountries: [{ name: string }] or [{ name: string, status: string }]
// inProgressCountries: [{ name: string }] or [string]
// plannedCountries: [{ name: string }] or [string]
// Matching is done against geo.properties.name from the TopoJSON — fully
// dynamic, no hardcoded ISO tables required. Any country name from the API
// will be reflected automatically as long as it matches the TopoJSON name.
const WorldOnboardingMap = ({
    onboardedCountries = [],
    inProgressCountries = [],
    plannedCountries = [],
    selectedCountry = "",
    onCountryClick = null,
}) => {
    const [tooltip, setTooltip] = useState({
        visible: false,
        x: 0,
        y: 0,
        name: "",
        status: null,
    });

    const [position, setPosition] = useState({ coordinates: [0, 8], zoom: 1 });

    const handleZoomIn = useCallback(() => {
        setPosition((pos) => ({
            ...pos,
            zoom: Math.min(Number((pos.zoom * 1.35).toFixed(2)), 8),
        }));
    }, []);

    const handleZoomOut = useCallback(() => {
        setPosition((pos) => ({
            ...pos,
            zoom: Math.max(Number((pos.zoom / 1.35).toFixed(2)), 0.8),
        }));
    }, []);

    const handleResetZoom = useCallback(() => {
        setPosition({ coordinates: [0, 8], zoom: 1 });
    }, []);

    const handleMoveEnd = useCallback((pos) => {
        setPosition(pos);
    }, []);

    // Normalised Map of country names to their status & original name — O(1) lookup
    const countryStatusMap = useMemo(() => {
        const map = new Map();

        const addCountry = (rawName, status) => {
            const norm = normalizeCountryName(rawName);
            if (norm) {
                map.set(norm, { status, originalName: rawName });
            }
        };

        // 1. Planned countries
        plannedCountries.forEach((c) => {
            const name = typeof c === "string" ? c : c?.name;
            if (name) addCountry(name, "planned");
        });

        // 2. In-progress countries
        inProgressCountries.forEach((c) => {
            const name = typeof c === "string" ? c : c?.name;
            if (name) addCountry(name, "inprogress");
        });

        // 3. Onboarded countries (can also specify status)
        onboardedCountries.forEach((c) => {
            const name = typeof c === "string" ? c : c?.name;
            if (!name) return;
            const status = (c?.status || "").toLowerCase().replace(/[\s_-]+/g, "");
            if (status === "inprogress" || status === "progress") {
                addCountry(name, "inprogress");
            } else if (status === "planned") {
                addCountry(name, "planned");
            } else {
                addCountry(name, "onboarded");
            }
        });

        return map;
    }, [onboardedCountries, inProgressCountries, plannedCountries]);

    // Count of onboarded countries
    const onboardedCount = useMemo(() => {
        let count = 0;
        countryStatusMap.forEach((entry) => {
            if (entry?.status === "onboarded") count++;
        });
        return count;
    }, [countryStatusMap]);

    const handleMouseEnter = useCallback(
        (geo, evt) => {
            const geoName = geo.properties?.name || "";
            const entry = countryStatusMap.get(normalizeCountryName(geoName));
            const status = entry?.status || null;
            const rect =
                evt.currentTarget
                    .closest(".wom-map-wrapper")
                    ?.getBoundingClientRect() || { left: 0, top: 0 };
            setTooltip({
                visible: true,
                x: evt.clientX - rect.left,
                y: evt.clientY - rect.top,
                name: geoName || "Unknown",
                status,
            });
        },
        [countryStatusMap]
    );

    const handleMouseMove = useCallback((evt) => {
        const rect =
            evt.currentTarget
                .closest(".wom-map-wrapper")
                ?.getBoundingClientRect() || { left: 0, top: 0 };
        setTooltip((prev) => ({
            ...prev,
            x: evt.clientX - rect.left,
            y: evt.clientY - rect.top,
        }));
    }, []);

    const handleMouseLeave = useCallback(() => {
        setTooltip((prev) => ({ ...prev, visible: false }));
    }, []);

    const handleCountryClick = useCallback(
        (geo) => {
            const geoName = geo.properties?.name || "";
            const normName = normalizeCountryName(geoName);
            const entry = countryStatusMap.get(normName);
            if (entry?.status === "onboarded" && onCountryClick) {
                onCountryClick(entry.originalName || geoName);
            }
        },
        [countryStatusMap, onCountryClick]
    );

    return (
        <div className="wom-container">
            {/* ── Header ── */}
            <div className="wom-header">
                <div className="wom-header-left">
                    <h2 className="wom-title">Live Markets</h2>
                </div>
                <div className="wom-badge">
                    <span className="wom-badge-dot" />
                    <span className="wom-badge-text">
                        {onboardedCount}{" "}
                        {onboardedCount === 1 ? "Country" : "Countries"}{" "}
                        Onboarded
                    </span>
                </div>
            </div>

            {/* ── Map ── */}
            <div className="wom-map-wrapper">
                {/* ── Floating Zoom / Pan Controls ── */}
                <div className="wom-zoom-controls">
                    <button
                        type="button"
                        className="wom-zoom-btn"
                        onClick={handleZoomIn}
                        title="Zoom In"
                        aria-label="Zoom In"
                    >
                        +
                    </button>
                    <div className="wom-zoom-divider" />
                    <button
                        type="button"
                        className="wom-zoom-btn"
                        onClick={handleZoomOut}
                        title="Zoom Out"
                        aria-label="Zoom Out"
                    >
                        −
                    </button>
                    <div className="wom-zoom-divider" />
                    <button
                        type="button"
                        className="wom-zoom-btn wom-zoom-btn--reset"
                        onClick={handleResetZoom}
                        title="Reset View"
                        aria-label="Reset View"
                    >
                        ⟲
                    </button>
                </div>

                <ComposableMap
                    projection="geoEqualEarth"
                    width={1000}
                    height={460}
                    projectionConfig={{ scale: 180, center: [0, 8] }}
                    className="wom-composable-map"
                >
                    <ZoomableGroup
                        zoom={position.zoom}
                        center={position.coordinates}
                        onMoveEnd={handleMoveEnd}
                        minZoom={0.8}
                        maxZoom={8}
                    >
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map((geo) => {
                                    const geoName = (geo.properties?.name || "").toLowerCase().trim();
                                    const normName = normalizeCountryName(geoName);
                                    const entry = countryStatusMap.get(normName);
                                    const status = entry?.status || null;
                                    const isSelected = Boolean(
                                        selectedCountry &&
                                        (normName === normalizeCountryName(selectedCountry) ||
                                         (entry?.originalName && entry.originalName.toLowerCase().trim() === selectedCountry.toLowerCase().trim()))
                                    );
                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            onClick={() => handleCountryClick(geo)}
                                            onMouseEnter={(evt) =>
                                                handleMouseEnter(geo, evt)
                                            }
                                            onMouseMove={handleMouseMove}
                                            onMouseLeave={handleMouseLeave}
                                            style={getGeoStyle(status, isSelected)}
                                        />
                                    );
                                })
                            }
                        </Geographies>
                    </ZoomableGroup>
                </ComposableMap>

                {/* ── Tooltip ── */}
                {tooltip.visible && (
                    <div
                        className={`wom-tooltip ${tooltip.status === "onboarded"
                            ? "wom-tooltip--onboarded"
                            : tooltip.status === "inprogress"
                                ? "wom-tooltip--inprogress"
                                : tooltip.status === "planned"
                                    ? "wom-tooltip--planned"
                                    : "wom-tooltip--default"
                            }`}
                        style={{
                            "--wom-x": `${tooltip.x + 14}px`,
                            "--wom-y": `${tooltip.y - 10}px`,
                        }}
                    >
                        <span className="wom-tooltip-name">{tooltip.name}</span>
                        <span
                            className={`wom-tooltip-status ${tooltip.status === "onboarded"
                                ? "wom-tooltip-status--on"
                                : tooltip.status === "inprogress"
                                    ? "wom-tooltip-status--inprogress"
                                    : tooltip.status === "planned"
                                        ? "wom-tooltip-status--planned"
                                        : "wom-tooltip-status--off"
                                }`}
                        >
                            {tooltip.status === "onboarded"
                                ? "✓ Onboarded (Click to select)"
                                : tooltip.status === "inprogress"
                                    ? "⏳ In Progress"
                                    : tooltip.status === "planned"
                                        ? "📋 Planned"
                                        : "Not Onboarded"}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Legend ── */}
            <div className="wom-legend">
                <div className="wom-legend-item">
                    <span className="wom-legend-dot wom-legend-dot--onboarded" />
                    <span className="wom-legend-label">Onboarded</span>
                </div>
                <div className="wom-legend-item">
                    <span className="wom-legend-dot wom-legend-dot--inprogress" />
                    <span className="wom-legend-label">In Progress</span>
                </div>
                <div className="wom-legend-item">
                    <span className="wom-legend-dot wom-legend-dot--planned" />
                    <span className="wom-legend-label">Planned</span>
                </div>
                <div className="wom-legend-hint">Click country to select · Scroll to zoom · Drag to pan</div>
            </div>
        </div>
    );
};

export default WorldOnboardingMap;
